import { Injectable, signal, computed, Type } from '@angular/core';

/**
 * Configuration for opening a sidebar with a component
 */
export interface SidebarConfig {
  /** The component type to render in the sidebar */
  component: Type<unknown>;
  /** Optional inputs to pass to the component */
  inputs?: Record<string, unknown>;
  /** Optional width override */
  width?: string;
  /** Mode: 'push' shrinks content, 'overlay' shows backdrop */
  mode?: 'push' | 'overlay';
}

/**
 * Service to control sidebar visibility and content.
 *
 * This service provides a signal-based API for managing the sidebar state,
 * enabling route-driven or programmatic sidebar control.
 *
 * @example
 * ```typescript
 * // In a component or route resolver
 * private sidebarService = inject(SidebarService);
 *
 * // Open sidebar with a component
 * this.sidebarService.open({
 *   component: MyDetailsSidebarComponent,
 *   inputs: { itemId: '123' }
 * });
 *
 * // Close sidebar
 * this.sidebarService.close();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SidebarService {
  // Internal state
  private readonly _isOpen = signal(false);
  private readonly _config = signal<SidebarConfig | null>(null);

  // Public readonly signals
  /** Whether the sidebar is currently open */
  readonly isOpen = this._isOpen.asReadonly();

  /** Current sidebar configuration (component, inputs, etc.) */
  readonly config = this._config.asReadonly();

  /** The component to render in the sidebar */
  readonly component = computed(() => this._config()?.component ?? null);

  /** Inputs to pass to the sidebar component */
  readonly inputs = computed(() => this._config()?.inputs ?? {});

  /** Sidebar width (default: 320px) */
  readonly width = computed(() => this._config()?.width ?? '320px');

  /** Sidebar mode (default: push) */
  readonly mode = computed(() => this._config()?.mode ?? 'push');

  /**
   * Open the sidebar with the specified configuration
   */
  open(config: SidebarConfig): void {
    this._config.set(config);
    this._isOpen.set(true);
  }

  /**
   * Close the sidebar and clear its configuration
   */
  close(): void {
    this._isOpen.set(false);
    // Keep config briefly for exit animation, then clear
    setTimeout(() => {
      if (!this._isOpen()) {
        this._config.set(null);
      }
    }, 200);
  }

  /**
   * Toggle the sidebar open/closed state
   */
  toggle(): void {
    if (this._isOpen()) {
      this.close();
    } else if (this._config()) {
      this._isOpen.set(true);
    }
  }

  /**
   * Update the current sidebar inputs without closing
   */
  updateInputs(inputs: Record<string, unknown>): void {
    const current = this._config();
    if (current) {
      this._config.set({ ...current, inputs: { ...current.inputs, ...inputs } });
    }
  }
}
